import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  FileText,
  Send,
  Download,
  MoreVertical,
  Brain,
  TrendingUp,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  generateInvoiceItems,
  suggestInvoicePricing,
  generatePaymentTerms,
} from '../../lib/ai/invoiceAssistant';
import { motion } from 'framer-motion';

interface Invoice {
  id: string;
  number: string;
  client: string;
  amount: number;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  items?: Array<{
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
}

const mockInvoices: Invoice[] = [
  {
    id: '1',
    number: 'INV-2024-001',
    client: 'Acme Corp',
    amount: 2500.0,
    dueDate: '2024-03-30',
    status: 'sent',
    items: [
      {
        description: 'Website Development',
        quantity: 1,
        rate: 2000,
        amount: 2000,
      },
      {
        description: 'Content Migration',
        quantity: 10,
        rate: 50,
        amount: 500,
      },
    ],
  },
  {
    id: '2',
    number: 'INV-2024-002',
    client: 'Tech Solutions Ltd',
    amount: 1800.0,
    dueDate: '2024-03-25',
    status: 'draft',
    items: [
      {
        description: 'Monthly Maintenance',
        quantity: 1,
        rate: 1500,
        amount: 1500,
      },
      {
        description: 'Additional Storage',
        quantity: 3,
        rate: 100,
        amount: 300,
      },
    ],
  },
];

export function InvoiceList() {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateAISuggestions = async (invoice: Invoice) => {
    setLoading(true);
    setError(null);
    try {
      const [items, pricing, terms] = await Promise.all([
        generateInvoiceItems(invoice.client),
        suggestInvoicePricing(invoice.items || [], {
          industry: 'technology',
          region: 'global',
          marketTrends: 'stable',
        }),
        generatePaymentTerms(
          {
            paymentHistory: 'good',
            relationshipDuration: '2 years',
          },
          invoice.amount
        ),
      ]);

      setAiSuggestions({ items, pricing, terms });
      setSelectedInvoice(invoice);
    } catch (err) {
      console.error('Error generating invoice insights:', err);
      setError('Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dark-card rounded-lg shadow">
      <div className="p-6 border-b border-dark-border">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-dark-text">Invoices</h2>
          <button className="px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2">
            <FileText className="h-4 w-4" />
            New Invoice
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-dark-bg">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-dark-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-dark-card divide-y divide-dark-border">
            {mockInvoices.map(invoice => (
              <tr key={invoice.id} className="hover:bg-dark-bg transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-text">
                  {invoice.number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  {invoice.client}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  ${invoice.amount.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-muted">
                  {format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                    ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' : ''}
                    ${invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' : ''}
                    ${invoice.status === 'draft' ? 'bg-gray-100 text-gray-800' : ''}
                    ${invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : ''}
                  `}
                  >
                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => generateAISuggestions(invoice)}
                      className="text-primary-400 hover:text-primary-300"
                    >
                      <Brain className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <Send className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <Download className="h-4 w-4" />
                    </button>
                    <button className="text-dark-muted hover:text-dark-text">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedInvoice && (
        <div className="p-6 border-t border-dark-border">
          <div className="bg-dark-bg rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-dark-text flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary-400" />
                AI Insights for Invoice {selectedInvoice.number}
              </h3>
              {loading && <Loader2 className="h-5 w-5 animate-spin text-primary-400" />}
            </div>

            {error ? (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="h-5 w-5" />
                <p>{error}</p>
              </div>
            ) : (
              aiSuggestions && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    <h4 className="text-dark-text font-medium">Suggested Items</h4>
                    <ul className="space-y-2">
                      {aiSuggestions.items.map((item: any, index: number) => (
                        <li key={index} className="text-dark-muted text-sm">
                          • {item.description} (${item.rate}/unit)
                        </li>
                      ))}
                    </ul>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-4"
                  >
                    <h4 className="text-dark-text font-medium">Pricing Analysis</h4>
                    <div className="space-y-2 text-sm">
                      <p className="text-dark-muted">
                        Market Average: ${aiSuggestions.pricing.marketAverage}
                      </p>
                      <p className="text-dark-muted">
                        Suggested Range: ${aiSuggestions.pricing.minRate} - $
                        {aiSuggestions.pricing.maxRate}
                      </p>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary-400" />
                        <span className="text-dark-muted">{aiSuggestions.pricing.trend}</span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-4"
                  >
                    <h4 className="text-dark-text font-medium">Payment Terms</h4>
                    <div className="text-sm text-dark-muted">
                      <p>{aiSuggestions.terms}</p>
                    </div>
                  </motion.div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
